import { useMemo } from 'react';

import type { TaskView, WorkflowView } from '@/lib/api/generated/model';

import { summarizeWorkspaceTasks, type QueuedRow } from './workspaceSummary';

const NO_QUEUE: readonly QueuedRow[] = [];

/**
 * The workspace's waiting runs, in the order they will start (spec 118 R3), as a
 * thunk: the capacity meter reads it only when its popover opens, so a board that
 * nobody asks about never summarises its workspace a second time.
 *
 * Memoised on the aggregate's arrays, which the query replaces on every refresh —
 * so the list is re-derived exactly when the figures beside it change.
 */
export function useWorkspaceQueue(
  workspace:
    { workflows: WorkflowView[]; tasks: TaskView[] } | null | undefined,
): () => readonly QueuedRow[] {
  const workflows = workspace?.workflows;
  const tasks = workspace?.tasks;
  return useMemo(() => {
    let cached: readonly QueuedRow[] | undefined;
    return () => {
      if (!workflows || !tasks) return NO_QUEUE;
      cached ??= summarizeWorkspaceTasks(workflows, tasks).queued;
      return cached;
    };
  }, [workflows, tasks]);
}
